import { Injectable } from '@angular/core';
import { CourseCustomer } from '@app/models';
import {
  SystemAccountRole,
  SystemComponent,
} from '@app/models/system/system-role';
import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject, Observable } from 'rxjs';
import { WebsocketService } from '../websocket.service';

export interface Credentials {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  contact: any;
  email: string;
  id: string;
  is_active: boolean;
  is_password_expired: boolean;
  is_sysadmin: boolean;
  // mandanten: any;
  access_token: string;
  user_roles: SystemAccountRole[];
  navigation_components: SystemComponent[];
  username: string;
  // selected_mandant: Mandant;
  theme: 'default' | 'light' | 'dark';
  customer: CourseCustomer;
  force_logout_after_minutes: number;
  inactive_after_minutes: number;
}

export interface RequiredPermission {
  module: string;
  component: string;
  permission: number;
}

const credentialsKey = 'credentials';

export const PermissionTypes = {
  NONE: 0,
  READ: 1,
  WRITE: 2,
  DELETE: 4,
};

export const PermissionModules = {
  CONTACT: 'CONTACTS',
  ERP: 'Erp',
  FLEET: 'FLEET',
  ITEMS: 'ITEMS',
  REPORTS: 'REPORTS',
  SYSTEM: 'SYSTEM',
  PROJECT_MANAGEMENT: 'PROJECT_MANAGEMENT',
};

export const PermissionComponents = {
  CONTACTS: 'LIST_CONTACTS',
  TIME: 'TIME',
  FLEET: 'FLEET',
  ERP: 'ERP',
  PROJECTS: 'LIST_PROJECTS',
  ITEMS: 'LIST_ITEMS',
  BILLINGS: 'LIST_BILLINGS',
  INVOICES: 'LIST_INVOICES',
  PURCHASE_ORDERS: 'LIST_PURCHASE_ORDERS',
  ERP_ASSETS: 'LIST_ERP_ASSETS',
  ERP_VOUCHERS: 'LIST_ERP_VOUCHERS',
  ERP_SETTINGS: 'LIST_ERP_SETTINGS',
  CORRESPONDENCES: 'LIST_CORRESPONDENCES',
  SYSTEM_ACCOUNTS: 'LIST_SYSTEM_ACCOUNTS',
  SYSTEM: 'SYSTEM',
  SYSTEM_ROLES: 'LIST_SYSTEM_ROLES',
  PROJECT_MANAGEMENT: 'PROJECT_MANAGEMENT',
};

/**
 * Provides storage for authentication credentials.
 * The Credentials interface should be replaced with proper implementation.
 */
@Injectable({
  providedIn: 'root',
})
export class CredentialsService {
  private _credentials: Credentials | null = null;
  private _credentials$ = new BehaviorSubject<Credentials | null>(
    this._credentials,
  );
  public forceLogoutWarning$ = new BehaviorSubject<boolean>(false);

  // private _mandant: Mandant = null;
  // private _mandant$ = new BehaviorSubject<Mandant>(this._mandant);

  constructor(
    private cookieService: CookieService,
    private websocketService: WebsocketService,
  ) {
    const savedCredentials =
      sessionStorage.getItem(credentialsKey) ||
      localStorage.getItem(credentialsKey);
    if (savedCredentials) {
      this._credentials = JSON.parse(savedCredentials);
      this._credentials$.next(this._credentials);
      // if (this._credentials.selected_mandant) {
      //   this._mandant = this._credentials.selected_mandant;
      //   this._mandant$.next(this._mandant);
      // }
      // let headers = new HttpHeaders();
      // headers = headers.append('Authorization', 'Bearer ' + this.token);
      // this.scCoreService.setHttpHeaders(headers);

      this.websocketService.openGlobalWebsocket(this.token);
    } else {
      const token = this.cookieService.get('token');
      if (token) {
        //todo hier user für token laden
        this._credentials = <Credentials>{};
        if (this._credentials) {
          this._credentials.access_token = token;
        }
      }
    }
  }

  /**
   * Checks is the user is authenticated.
   * @return True if the user is authenticated.
   */
  isAuthenticated(): boolean {
    return !!this.credentials;
  }

  /**
   * Gets the user credentials.
   * @return The user credentials or null if the user is not authenticated.
   */
  get credentials(): Credentials | null {
    return this._credentials;
  }

  /**
   * Gets the user credentials.
   * @return The user credentials or null if the user is not authenticated.
   */
  get $credentials(): Observable<Credentials | null> {
    return this._credentials$.asObservable();
  }

  /**
   * Gets the user credentials.
   * @return The user credentials or null if the user is not authenticated.
   */
  // get mandant(): Mandant {
  //   return this._mandant;
  // }

  /**
   * Gets the user credentials.
   * @return The user credentials or null if the user is not authenticated.
   */
  // get $mandant(): Observable<Mandant> {
  //   return this._mandant$.asObservable();
  // }

  /**
   * Gets the token from the user credentials.
   * @return The token or '' if the user is not authenticated.
   */
  get token(): string {
    return this._credentials ? this._credentials.access_token : '';
  }

  /**
   * Gets the user credentials.
   * @return The user credentials or null if the user is not authenticated.
   */
  get remember(): boolean {
    return localStorage.getItem(credentialsKey) != undefined;
  }

  /**
   * Sets the user credentials.
   * The credentials may be persisted across sessions by setting the `remember` parameter to true.
   * Otherwise, the credentials are only persisted for the current session.
   * @param credentials The user credentials.
   * @param remember True to remember credentials across sessions.
   */
  setCredentials(credentials?: Credentials, remember?: boolean) {
    this._credentials = credentials || null;

    if (credentials) {
      const storage = remember ? localStorage : sessionStorage;
      const tmpCredentials: Credentials = {
        ...credentials,
        email: '',
        username: '',
      };
      storage.setItem(credentialsKey, JSON.stringify(tmpCredentials));
      // let headers = new HttpHeaders();
      // headers = headers.append('Authorization', 'Bearer ' + this.token);
      // this.scCoreService.setHttpHeaders(headers);

      this.websocketService.openGlobalWebsocket(this.token);
      if (remember) {
        this.cookieService.set(
          'token',
          this._credentials ? this._credentials.access_token : '',
          0,
          '',
          window.location.hostname.substring(
            window.location.hostname.indexOf('.') + 1,
          ),
        );
      }

      // if (this._credentials.selected_mandant) {
      //   this._mandant = this._credentials.selected_mandant;
      // }
    } else {
      this.cookieService.deleteAll();
      sessionStorage.removeItem(credentialsKey);
      localStorage.removeItem(credentialsKey);
      localStorage.removeItem('forceLogout');
      // this.scCoreService.setHttpHeaders(undefined);
      this.websocketService.closeGlobalWebsocket();
    }

    // this._mandant$.next(this._mandant);
    // this._credentials$.next(this._credentials);
  }

  // setMandant(mandant: Mandant) {
  //   this._mandant = mandant || null;

  //   if (this.credentials && !this.credentials.selected_mandant) {
  //     this._credentials.selected_mandant = mandant;
  //     this._credentials$.next(this._credentials);
  //   }
  //   this._mandant$.next(this._mandant);
  // }

  checkPermissions(requiredPermissions: RequiredPermission[]) {
    const user = this.credentials;
    if (
      !requiredPermissions ||
      requiredPermissions.length == 0 ||
      user?.is_sysadmin
    ) {
      return true;
    }
    if (user && user.id && user.user_roles) {
      for (const requiredPermission of requiredPermissions) {
        let permissionMatched = false;
        for (const role of user.user_roles) {
          if (role.role.modules) {
            for (const module of role.role.modules) {
              if (module.module.module_name == requiredPermission.module) {
                if (
                  this.checkPermission(
                    module.permission,
                    requiredPermission.permission,
                  )
                ) {
                  permissionMatched = true;
                } else {
                  for (const component of module.components) {
                    if (
                      component.component.component_name ==
                        requiredPermission.component &&
                      this.checkPermission(
                        component.permission,
                        requiredPermission.permission,
                      )
                    ) {
                      permissionMatched = true;
                    }
                  }
                }
              }
            }
          }
        }
        if (!permissionMatched) {
          return false;
        }
      }
    }
    return true;
  }

  checkPermission(currentPermission: number, permission: number) {
    const currentPermissionBin = currentPermission.toString(2);
    const permissionBin = permission.toString(2);
    return (
      currentPermissionBin.charAt(
        currentPermissionBin.length - permissionBin.length,
      ) == permissionBin.charAt(0)
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  hasPermission(module: string, component: string, permission: number) {
    return true;
  }
}

export function checkPermissionsFromStorage(
  requiredPermissions: RequiredPermission[],
) {
  const user = localStorage.getItem('credentials')
    ? JSON.parse(<string>localStorage.getItem('credentials'))
    : JSON.parse(<string>sessionStorage.getItem('credentials'));

  if (
    !requiredPermissions ||
    requiredPermissions.length == 0 ||
    user.is_sysadmin
  ) {
    return true;
  }
  if (user && user.id && user.user_roles) {
    for (const requiredPermission of requiredPermissions) {
      let permissionMatched = false;
      for (const role of user.user_roles) {
        if (role.role.modules) {
          for (const module of role.role.modules) {
            if (module.module.module_name == requiredPermission.module) {
              if (
                checkPermission(
                  module.permission,
                  requiredPermission.permission,
                )
              ) {
                permissionMatched = true;
              } else {
                for (const component of module.components) {
                  if (
                    component.component.component_name ==
                      requiredPermission.component &&
                    checkPermission(
                      component.permission,
                      requiredPermission.permission,
                    )
                  ) {
                    permissionMatched = true;
                  }
                }
              }
            }
          }
        }
      }
      if (!permissionMatched) {
        return false;
      }
    }
  }
  return true;
}

export function checkPermission(currentPermission: number, permission: number) {
  const currentPermissionBin = currentPermission.toString(2);
  const permissionBin = permission.toString(2);
  return (
    currentPermissionBin.charAt(
      currentPermissionBin.length - permissionBin.length,
    ) == permissionBin.charAt(0)
  );
}
